// One line of the session's log. `at` is wall clock rather than a monotonic reading, since what the second tab is for
// is telling somebody when a thing happened relative to the rest of what they did.
export interface LogEntry {
  at: number;
  level: LogLevel;
  text: string;
}

export type LogLevel = "engine" | "query" | "notice" | "error";

// The oldest lines are dropped rather than the newest refused: a log that stopped recording is a log nobody can trust.
export const MAX_LOG_ENTRIES = 10_000;

// Appends within the cap, returning the same array when nothing changed so a render can be skipped on identity.
export function appended(entries: readonly LogEntry[], added: readonly LogEntry[]): LogEntry[] {
  if (added.length === 0) return entries as LogEntry[];
  const next = [...entries, ...added];
  return next.length > MAX_LOG_ENTRIES ? next.slice(next.length - MAX_LOG_ENTRIES) : next;
}

// The whole log as one string, because ten thousand rows of elements is a tab that takes a second to open and this
// is a text node the browser lays out in one pass. /python's output panel is the same trade for the same reason.
export function writeLog(entries: readonly LogEntry[]): string {
  return entries.map(writeEntry).join("\n");
}

export function writeEntry(entry: LogEntry): string {
  return `${clockOf(entry.at)}  ${LEVEL_MARKS[entry.level]}  ${entry.text}`;
}

// Local time to the millisecond, which is the resolution a statement that took two of them is read at.
function clockOf(at: number): string {
  const time = new Date(at);
  const parts = [time.getHours(), time.getMinutes(), time.getSeconds()].map((part) => String(part).padStart(2, "0"));
  return `${parts.join(":")}.${String(time.getMilliseconds()).padStart(3, "0")}`;
}

// Padded to one width so the text of every line starts in the same column, which is what makes a log skimmable.
const LEVEL_MARKS: Record<LogLevel, string> = {
  engine: "ENGINE",
  query: "QUERY ",
  notice: "NOTICE",
  error: "ERROR ",
};
